import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  ChatMessageList,
  ChatInput,
  BookSelector,
  SessionHistorySidebar,
} from "@/components/chat";
import {
  sendChatMessageStream,
  sendAskAiMessageStream,
  fetchUserChatSessions,
  fetchChatMessages,
  deleteChatSession,
  submitMessageFeedback,
} from "@/api/chat";
import type { ChatMessage, ChatSession } from "@/types/chat";

const PERSONAS = [
  { value: "auto", label: "Auto", description: "Auto-detect best persona" },
  { value: "librarian", label: "Librarian", description: "Book recommendations & discovery" },
  { value: "reading_coach", label: "Reading Coach", description: "Goals, progress & motivation" },
  { value: "culture_guide", label: "Culture Guide", description: "Mizo cultural context" },
  { value: "english_coach", label: "English Coach", description: "English learning & translation" },
  { value: "life_guide", label: "Life Guide", description: "Life advice & emotional support" },
];

const PERSONA_LABELS: Record<string, string> = {
  librarian: "Librarian",
  reading_coach: "Reading Coach",
  culture_guide: "Culture Guide",
  english_coach: "English Coach",
  life_guide: "Life Guide",
};

const READING_COACH_PATTERNS = /\b(chhiar|read|goal|tum|habit|ni tin|minute|progress|milestone|motivation|fuih|chhiar zel|chhiar duh|chhiar tur|chhiar tum|lehkhabu chhiar|eng zat|reading)\b/i;
const CULTURE_GUIDE_PATTERNS = /\b(tlawmngaihna|khawtlang|hnatlang|zawlbuk|thianghlim|mizo culture|mizo history|mizo nun|hnam|pi leh pu|tawnthu|thusawi|sakhming|nunphung|festival|kut|heritage|tradition|proverb|lunglen|kristian|zai|lam|culture|hnam dang|hmar|lai |mara|paite)\b/i;

function detectPersona(query: string): string {
  const cultureScore = (query.match(CULTURE_GUIDE_PATTERNS) || []).length;
  const readingScore = (query.match(READING_COACH_PATTERNS) || []).length;

  if (cultureScore > 0 && cultureScore >= readingScore) return "culture_guide";
  if (readingScore > 0) return "reading_coach";
  return "librarian";
}

type ChatMode = "book" | "ask_ai";

export default function AIChatPlayground() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [chatMode, setChatMode] = useState<ChatMode>("book");
  const [selectedBookId, setSelectedBookId] = useState<number | undefined>();
  const [selectedPersona, setSelectedPersona] = useState("auto");
  const [activeSessionId, setActiveSessionId] = useState<number | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingPersona, setStreamingPersona] = useState<string | undefined>();
  // Track which persona answered each assistant message (messageId -> persona slug)
  const [messagePersonas, setMessagePersonas] = useState<Record<number, string>>({});

  // Fetch user's sessions for sidebar
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["user-chat-sessions", chatMode, selectedBookId],
    queryFn: () =>
      chatMode === "book"
        ? fetchUserChatSessions(selectedBookId, "book")
        : fetchUserChatSessions(undefined, "ask_ai"),
    enabled: chatMode === "ask_ai" || !!selectedBookId,
  });

  const handleSelectSession = useCallback(
    async (sessionId: number) => {
      setActiveSessionId(sessionId);
      try {
        const msgs = await fetchChatMessages(sessionId);
        setMessages(msgs);
      } catch {
        setMessages([]);
      }
    },
    []
  );

  const handleNewChat = useCallback(() => {
    setActiveSessionId(undefined);
    setMessages([]);
  }, []);

  const handleDeleteSession = useCallback(
    async (sessionId: number) => {
      try {
        await deleteChatSession(sessionId);
        if (activeSessionId === sessionId) {
          setActiveSessionId(undefined);
          setMessages([]);
        }
        queryClient.invalidateQueries({ queryKey: ["user-chat-sessions"] });
      } catch (err) {
        console.error("Failed to delete session:", err);
      }
    },
    [activeSessionId, queryClient]
  );

  const handleModeChange = useCallback((mode: string) => {
    setChatMode(mode as ChatMode);
    setActiveSessionId(undefined);
    setMessages([]);
    setSelectedPersona("librarian");
  }, []);

  const handleFeedback = useCallback(
    async (messageId: number, feedback: 1 | -1 | null) => {
      // Optimistic update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, feedback, feedback_at: feedback ? new Date().toISOString() : null }
            : m
        )
      );
      try {
        await submitMessageFeedback(messageId, feedback);
      } catch {
        // Revert on error
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, feedback: m.feedback, feedback_at: m.feedback_at }
              : m
          )
        );
      }
    },
    []
  );

  const handleSend = useCallback(
    async (text: string) => {
      if (chatMode === "book" && !selectedBookId) return;

      // Optimistic: add user message
      const tempUserMsg: ChatMessage = {
        id: Date.now(),
        session_id: activeSessionId || 0,
        role: "user",
        content: text,
        sources: null,
        token_count: 0,
        feedback: null,
        feedback_at: null,
        feedback_comment: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMsg]);
      setIsLoading(true);
      setStreamingContent("");

      // Determine which persona to use
      const effectivePersona = selectedPersona === "auto"
        ? detectPersona(text)
        : selectedPersona;
      setStreamingPersona(effectivePersona);

      let streamedText = "";

      const onToken = (token: string) => {
        streamedText += token;
        setStreamingContent(streamedText);
      };

      try {
        if (chatMode === "ask_ai") {
          await sendAskAiMessageStream(
            {
              query: text,
              sessionId: activeSessionId,
              ...(!activeSessionId && { persona: selectedPersona === "auto" ? "auto" : effectivePersona }),
            },
            onToken,
            (meta) => {
              if (!activeSessionId && meta.sessionId) {
                setActiveSessionId(meta.sessionId);
                queryClient.invalidateQueries({ queryKey: ["user-chat-sessions"] });
              }
              const msgId = meta.messageId || Date.now() + 1;
              const assistantMsg: ChatMessage = {
                id: msgId,
                session_id: meta.sessionId,
                role: "assistant",
                content: streamedText,
                sources: meta.sources,
                token_count: 0,
                feedback: null,
                feedback_at: null,
                feedback_comment: null,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, assistantMsg]);
              // Use server-detected persona if available (from LLM classification), otherwise client-side
              const resolvedPersona = meta.persona || effectivePersona;
              setMessagePersonas((prev) => ({ ...prev, [msgId]: resolvedPersona }));
              setStreamingContent("");
              setStreamingPersona(undefined);
              setIsLoading(false);
            },
            (errorMsg) => {
              const errMessage: ChatMessage = {
                id: Date.now() + 1,
                session_id: activeSessionId || 0,
                role: "assistant",
                content: `Error: ${errorMsg}`,
                sources: null,
                token_count: 0,
                feedback: null,
                feedback_at: null,
                feedback_comment: null,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, errMessage]);
              setStreamingContent("");
              setStreamingPersona(undefined);
              setIsLoading(false);
            },
          );
        } else {
          await sendChatMessageStream(
            {
              bookId: selectedBookId!,
              query: text,
              sessionId: activeSessionId,
            },
            onToken,
            (meta) => {
              if (!activeSessionId && meta.sessionId) {
                setActiveSessionId(meta.sessionId);
                queryClient.invalidateQueries({ queryKey: ["user-chat-sessions"] });
              }
              const assistantMsg: ChatMessage = {
                id: meta.messageId || Date.now() + 1,
                session_id: meta.sessionId,
                role: "assistant",
                content: streamedText,
                sources: meta.sources,
                token_count: 0,
                feedback: null,
                feedback_at: null,
                feedback_comment: null,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, assistantMsg]);
              setStreamingContent("");
              setIsLoading(false);
            },
            (errorMsg) => {
              const errMessage: ChatMessage = {
                id: Date.now() + 1,
                session_id: activeSessionId || 0,
                role: "assistant",
                content: `Error: ${errorMsg}`,
                sources: null,
                token_count: 0,
                feedback: null,
                feedback_at: null,
                feedback_comment: null,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, errMessage]);
              setStreamingContent("");
              setIsLoading(false);
            },
          );
        }
      } catch (err) {
        // Network-level error (before stream starts)
        const errorMsg: ChatMessage = {
          id: Date.now() + 1,
          session_id: activeSessionId || 0,
          role: "assistant",
          content: `Error: ${err instanceof Error ? err.message : "Failed to get response"}`,
          sources: null,
          token_count: 0,
          feedback: null,
          feedback_at: null,
          feedback_comment: null,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        setStreamingContent("");
        setIsLoading(false);
      }
    },
    [chatMode, selectedBookId, selectedPersona, activeSessionId, queryClient]
  );

  const isReady = chatMode === "ask_ai" || !!selectedBookId;

  return (
    <AdminLayout
      title="Ask AI"
      actionButton={
        <Button
          variant="outline"
          onClick={() => navigate("/admin/ask-ai")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sessions
        </Button>
      }
      mainClassName="flex-1 overflow-hidden p-0 bg-background"
    >
      <div className="flex h-full">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Bar: Mode toggle + Book + Language selectors */}
          <div className="flex items-center gap-3 p-3 border-b">
            <Tabs value={chatMode} onValueChange={handleModeChange}>
              <TabsList>
                <TabsTrigger value="book">Book</TabsTrigger>
                <TabsTrigger value="ask_ai">General</TabsTrigger>
              </TabsList>
            </Tabs>

            {chatMode === "book" && (
              <BookSelector
                value={selectedBookId}
                onChange={(id) => {
                  setSelectedBookId(id);
                  setActiveSessionId(undefined);
                  setMessages([]);
                }}
              />
            )}

            {chatMode === "ask_ai" && (
              <Select
                value={selectedPersona}
                onValueChange={setSelectedPersona}
                disabled={!!activeSessionId}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Persona" />
                </SelectTrigger>
                <SelectContent>
                  {PERSONAS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span>{p.label}</span>
                      <span className="text-xs text-muted-foreground ml-1.5">
                        — {p.description}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

          </div>

          {/* Messages */}
          <div className="flex-1 flex flex-col min-h-0">
            {!isReady ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Select a book to start chatting.
              </div>
            ) : (
              <ChatMessageList
                messages={messages}
                isLoading={isLoading}
                streamingContent={streamingContent}
                streamingPersonaLabel={streamingPersona ? PERSONA_LABELS[streamingPersona] : undefined}
                messagePersonaLabels={
                  selectedPersona === "auto"
                    ? Object.fromEntries(
                        Object.entries(messagePersonas).map(([id, slug]) => [id, PERSONA_LABELS[slug] || slug])
                      )
                    : undefined
                }
                onFeedback={handleFeedback}
              />
            )}
          </div>

          {/* Input */}
          <ChatInput
            onSend={handleSend}
            disabled={!isReady || isLoading}
            placeholder={
              chatMode === "ask_ai"
                ? "Ask me about books, get recommendations..."
                : selectedBookId
                ? "Ask a question about this book..."
                : "Select a book first"
            }
          />
        </div>

        {/* Session Sidebar */}
        {isReady && (
          <SessionHistorySidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            onDeleteSession={handleDeleteSession}
            isLoading={sessionsLoading}
          />
        )}
      </div>
    </AdminLayout>
  );
}
